import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

export default function BillingSuccessPage() {
    const { refreshUser, user } = useAuth();
    const navigate = useNavigate();
    const [attempts, setAttempts] = useState(0);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        let isMounted = true;

        const pollStatus = async () => {
            try {
                await refreshUser();

                // Check current status after refresh
                if (user?.subscription_status === "active") {
                    navigate("/episodes", { replace: true });
                    return;
                }

                if (attempts > 30) { // 30 seconds timeout (1s interval)
                    setError("It's taking longer than expected to confirm your subscription. Please check your email or try refreshing the page.");
                    return;
                }

                // Schedule next poll
                if (isMounted) {
                    setAttempts(prev => prev + 1);
                    timeoutId = setTimeout(pollStatus, 1000);
                }
            } catch (err) {
                console.error("Polling failed", err);
                // Continue polling even on transient errors
                if (isMounted && attempts <= 30) {
                    setAttempts(prev => prev + 1);
                    timeoutId = setTimeout(pollStatus, 1000);
                }
            }
        };

        // Initial poll
        timeoutId = setTimeout(pollStatus, 1000);

        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
        };
    }, [refreshUser, user?.subscription_status, navigate, attempts]);

    return (
        <div className={cn("min-h-screen flex items-center justify-center", theme.colors.backgroundCream)}>
            <div className={cn(
                "max-w-md w-full mx-4 p-8 rounded-2xl text-center",
                "bg-white dark:bg-gray-800",
                theme.borders.neubrutalism,
                theme.shadows.neubrutalism
            )}>
                {error ? (
                    <>
                        <div className="w-16 h-16 mx-auto mb-6 text-red-500">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-black mb-4 text-gray-900 dark:text-white">
                            Something went wrong
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300 mb-8">
                            {error}
                        </p>
                        <button
                            onClick={() => navigate("/paywall")}
                            className={cn("w-full py-3 font-semibold rounded-xl", theme.buttons.primary)}
                        >
                            Return to Plans
                        </button>
                    </>
                ) : (
                    <>
                        <div className="w-16 h-16 mx-auto mb-6 text-green-500 animate-bounce">
                            <CheckCircle2 className="w-full h-full" />
                        </div>
                        <h2 className="text-2xl font-black mb-4 text-gray-900 dark:text-white">
                            Confirming Subscription...
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300 mb-8">
                            Please wait while we verify your payment. This usually takes just a few seconds.
                        </p>
                        <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700 overflow-hidden">
                            <div
                                className="bg-green-500 h-2.5 rounded-full transition-all duration-1000 ease-out"
                                style={{ width: `${Math.min((attempts / 10) * 100, 90)}%` }}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
